import type { ReactNode } from 'react'

import { customMedia } from '@space-core/styles'
import { ShowcaseOrientation } from '@space-shell/common/services/membership'
import { SectionType } from '@space-shell/common/services/sections/enums'
import styled from 'styled-components'
import { Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide, type SwiperProps } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/keyboard'
import 'swiper/css/a11y'

import {
  FRACTION_TO_SHOW_NEXT_CARD,
  SLIDES_PER_GROUP_MOBILE_HORIZONTAL,
  SLIDES_PER_GROUP_MOBILE_VERTICAL,
  SLIDES_PER_PAGE_DEFAULT,
  SLIDES_PER_VIEW_MOBILE_HORIZONTAL,
  SLIDES_PER_VIEW_MOBILE_VERTICAL,
  SLIDES_PER_VIEW_MOBILE_MODULE,
  SLIDES_PER_GROUP_MOBILE_MODULE,
  TWO
} from './constants'

export type SwiperCardsProps = {
  children: ReactNode
  slidesPerPage?: number
  orientation?: ShowcaseOrientation
  sectionType?: SectionType
}

const Wrapper = styled.div<{ $sectionType?: SectionType }>`
  padding: 0 var(--spacer-10);

  :hover {
    z-index: 2;
  }

  ${customMedia.lessThan('md')`
     padding: 0 var(--spacer-4);
     overflow: hidden;
  `}

  .swiper,
  .swiper-container,
  .swiper-wrapper {
    overflow: unset;
  }

  .swiper-slide,
  .swiper-virtual,
  .swiper-backface-hidden {
    transform: unset !important;
  }

  .swiper-pagination-bullet {
    width: var(--spacer-3);
    height: var(--spacer-1);
    border-radius: var(--spacer-1);
    background-color: var(--gray-600);
  }

  .swiper-pagination {
    z-index: -1;
    opacity: 0;
    bottom: ${({ $sectionType }) =>
      $sectionType === SectionType.MODULE ? '-1.875rem !important' : '-1rem !important'};
    transition: opacity var(--timing) var(--easing) calc(var(--timing) / 2);
  }

  .swiper-pagination-bullet-active {
    width: var(--spacer-5);
    background-color: var(--gray-800);
    transition: width 0.8s var(--easing);
  }

  .swiper-button-disabled {
    visibility: hidden;
  }

  .swiper-button-next,
  .swiper-button-prev {
    opacity: 1;
    margin-top: -3rem;
    color: var(--white);
    height: ${({ $sectionType }) => ($sectionType === SectionType.MODULE ? '6.25rem' : '8.75rem')};
    width: var(--spacer-7);
    background-color: rgba(14, 16, 18, 0.9);
    transition: opacity var(--timing) var(--easing) calc(var(--timing) / 2);

    &:after {
      font-size: var(--spacer-5);
    }

    &:hover {
      &:after {
        font-size: var(--spacer-6);
        transition: all var(--timing) var(--easing) calc(var(--timing) / 2);
      }
    }
  }

  .swiper-button-next {
    right: -1.8rem;
    transform: translateX(100%);
    border-top-left-radius: var(--border-radius-lg);
    border-bottom-left-radius: var(--border-radius-lg);
    padding-left: 1.18rem;
    padding-right: 1.18rem;

    &:hover {
      right: -0.9rem;
      padding-left: 1.75rem;
      padding-right: 1.75rem;
      transition: all 0.8s var(--easing) calc(var(--timing) / 2);
    }
  }

  .swiper-button-prev {
    left: -1.8rem;
    padding-left: 1.125rem;
    padding-right: 1.125rem;
    transform: translateX(-100%);
    border-top-right-radius: var(--border-radius-lg);
    border-bottom-right-radius: var(--border-radius-lg);

    &:hover {
      left: -0.9rem;
      padding-left: 1.75rem;
      padding-right: 1.75rem;
      transition: all 0.8s var(--easing) calc(var(--timing) / 2);
    }
  }

  &:hover {
    .swiper-button-next,
    .swiper-button-prev,
    .swiper-pagination {
      opacity: 1;
    }
  }
`

const PreventHover = styled.div<{ $direction: 'prev' | 'next' }>`
  z-index: 1;
  top: 0;
  height: 100%;
  width: 4rem;
  position: absolute;
  ${({ $direction }) =>
    $direction === 'next' ? 'transform: translateX(100%)' : 'transform: translateX(-100%)'};
  ${({ $direction }) => ($direction === 'next' ? 'right: -8px' : 'left: -8px')};
`

const SwiperCards = ({
  children,
  orientation,
  slidesPerPage = SLIDES_PER_PAGE_DEFAULT,
  sectionType
}: SwiperCardsProps) => {
  const customOrientation =
    sectionType === SectionType.MODULE
      ? ShowcaseOrientation.HORIZONTAL
      : orientation || ShowcaseOrientation.VERTICAL

  const slidesPerViewMobileHorizontal =
    sectionType === SectionType.MODULE
      ? SLIDES_PER_VIEW_MOBILE_MODULE
      : SLIDES_PER_VIEW_MOBILE_HORIZONTAL

  const slidesPerGroupMobileHorizontal =
    sectionType === SectionType.MODULE
      ? SLIDES_PER_GROUP_MOBILE_MODULE
      : SLIDES_PER_GROUP_MOBILE_HORIZONTAL

  const SWIPER_PROPS: SwiperProps = {
    navigation: true,
    speed: 800,
    spaceBetween: 16,
    pagination: {
      clickable: true
    },
    breakpoints: {
      320: {
        slidesPerView:
          customOrientation === ShowcaseOrientation.HORIZONTAL
            ? slidesPerViewMobileHorizontal
            : SLIDES_PER_VIEW_MOBILE_VERTICAL,
        slidesPerGroup:
          customOrientation === ShowcaseOrientation.HORIZONTAL
            ? slidesPerGroupMobileHorizontal
            : SLIDES_PER_GROUP_MOBILE_VERTICAL,
        spaceBetween: 8
      },
      576: {
        slidesPerView: slidesPerPage - TWO + FRACTION_TO_SHOW_NEXT_CARD,
        slidesPerGroup: slidesPerPage - TWO + FRACTION_TO_SHOW_NEXT_CARD
      },
      770: {
        slidesPerView: slidesPerPage - 1,
        slidesPerGroup: slidesPerPage - 1,
        spaceBetween: 16
      },
      1000: {
        slidesPerView: slidesPerPage,
        slidesPerGroup: slidesPerPage
      },
      1600: {
        slidesPerView: slidesPerPage + 1,
        slidesPerGroup: slidesPerPage + 1
      }
    },
    modules: [Pagination, Navigation]
  }

  return (
    <Wrapper $sectionType={sectionType} className='_position-relative'>
      <Swiper {...SWIPER_PROPS}>
        {children}
        <PreventHover $direction='prev' />
        <PreventHover $direction='next' />
      </Swiper>
    </Wrapper>
  )
}

SwiperCards.Item = SwiperSlide

export default SwiperCards
